/**
 * Femcare.life — First-party UTM attribution capture (web only).
 *
 * Non-goals:
 *  - Do NOT touch iOS/Android AppsFlyer.
 *  - Do NOT capture PII. Only campaign metadata + landing page + referrer.
 *  - Attribution NEVER controls pricing, entitlement, verification. Informational only.
 *
 * Storage: localStorage. Two records:
 *   fc_attr_first  (immutable once set)
 *   fc_attr_last   (updated whenever a NEW campaign parameter arrives)
 * Both TTL = 90 days from write time.
 */

const STORAGE_FIRST = "fc_attr_first";
const STORAGE_LAST  = "fc_attr_last";
const TTL_MS        = 90 * 24 * 60 * 60 * 1000;
const MAX_LEN       = 256;

const UTM_KEYS = ["utm_source","utm_medium","utm_campaign","utm_content","utm_term"];
const CLICK_ID_KEYS = ["gclid","gbraid","wbraid","fbclid","msclkid"];
const PUB_KEYS = ["publisher_id","pid","sub_id","sub1","sub2","sub3"];
const ALL_KEYS = [...UTM_KEYS, ...CLICK_ID_KEYS, ...PUB_KEYS];

function sanitize(v) {
    if (v == null) return null;
    // Strip control chars, cap length. Never HTML/JS interpreted.
    const s = String(v).replace(/[\x00-\x1F\x7F]/g, "").trim().slice(0, MAX_LEN);
    return s || null;
}

function readStore(key) {
    try {
        const raw = localStorage.getItem(key);
        if (!raw) return null;
        const rec = JSON.parse(raw);
        if (!rec || typeof rec !== "object") return null;
        if (rec.expires_at && Date.now() > rec.expires_at) {
            localStorage.removeItem(key);
            return null;
        }
        return rec;
    } catch { return null; }
}

function writeStore(key, rec) {
    try {
        localStorage.setItem(key, JSON.stringify({ ...rec, expires_at: Date.now() + TTL_MS }));
    } catch { /* quota / private mode — silent no-op */ }
}

function parseFromLocation() {
    const params = new URLSearchParams(window.location.search);
    const out = {};
    let hasAnyCampaign = false;
    for (const k of ALL_KEYS) {
        const v = sanitize(params.get(k));
        if (v) {
            out[k] = v;
            if (UTM_KEYS.includes(k) || CLICK_ID_KEYS.includes(k) || PUB_KEYS.includes(k)) hasAnyCampaign = true;
        }
    }
    out.landing_page = sanitize(window.location.pathname + window.location.search) || sanitize(window.location.pathname);
    out.referrer     = sanitize(document.referrer || "") || null;
    out.captured_at  = new Date().toISOString();
    return { snapshot: out, hasAnyCampaign };
}

/**
 * Public: initialise on every page load / route change.
 * Idempotent. Preserves first-touch; refreshes last-touch when a NEW campaign arrives.
 */
export function initAttribution() {
    const { snapshot, hasAnyCampaign } = parseFromLocation();
    let changed = false;
    if (hasAnyCampaign) {
        if (!readStore(STORAGE_FIRST)) { writeStore(STORAGE_FIRST, snapshot); changed = true; }
        writeStore(STORAGE_LAST, snapshot);
        changed = true;
    } else if (!readStore(STORAGE_LAST)) {
        // First visit with NO campaign params — record "direct" landing only, no campaign fields.
        writeStore(STORAGE_LAST, snapshot);
        changed = true;
    }
    // Fire-and-forget push to the backend so the currently-authenticated ft_user_id
    // (if any) gets the latest attribution. Anonymous visitors → silent 401.
    if (changed) _pushToBackend();
}

async function _pushToBackend() {
    try {
        const base = process.env.REACT_APP_BACKEND_URL || "";
        const body = { first: readStore(STORAGE_FIRST), last: readStore(STORAGE_LAST) };
        if (!body.first && !body.last) return;
        await fetch(base + "/api/web/attribution/attach", {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
            keepalive: true,
        });
    } catch (_e) { /* silent */ }
}

/**
 * Public: get the current snapshot to send to the backend.
 * Shape (all fields optional; nulls stripped):
 *   { first: {utm_*, gclid/…, publisher_id/…, landing_page, referrer, captured_at},
 *     last:  {…same fields…} }
 */
export function getAttribution() {
    return { first: readStore(STORAGE_FIRST), last: readStore(STORAGE_LAST) };
}

/** Testing helper — never called from prod code. */
export function _clearAttribution() {
    try { localStorage.removeItem(STORAGE_FIRST); localStorage.removeItem(STORAGE_LAST); } catch {}
}
